import React, { useLayoutEffect, useRef, useState } from 'react';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Nav, NavItem, NavLink } from 'reactstrap';
import classnames from 'classnames';

export interface ITabItem {
  id: string;
  label: string;
  icon?: string;
}

interface Props {
  items: ITabItem[];
  activeTab: string;
  onSelect: (id: string) => void;
}

const MORE_WIDTH = 56;

/**
 * Tabs that always stay on a single row: the tabs that do not fit are moved into a "..." dropdown
 * that opens on hover. The active tab is always kept in the row.
 *
 * Every tab is always rendered, so its real width can be measured. The ones that do not fit
 * are taken out of the flow (and hidden) instead of being removed.
 */
const OverflowTabs: React.FC<Props> = ({ items, activeTab, onSelect }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [menuOpen, setMenuOpen] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const calculate = () => {
      const tabs = Array.from(container.querySelectorAll<HTMLElement>('li.overflow-tab'));
      const widths = tabs.map(tab => tab.offsetWidth);
      const available = container.clientWidth;
      if (widths.reduce((sum, w) => sum + w, 0) <= available) {
        setVisibleCount(items.length);
        return;
      }
      // the active tab is always shown, so its width is reserved first
      const activeIndex = items.findIndex(item => item.id === activeTab);
      let used = MORE_WIDTH + (activeIndex >= 0 ? widths[activeIndex] : 0);
      let count = 0;
      while (count < widths.length && (count === activeIndex || used + widths[count] <= available)) {
        if (count !== activeIndex) used += widths[count];
        count++;
      }
      setVisibleCount(count);
    };

    calculate();
    // icons and fonts load after the first render and change the tab widths
    const observer = new ResizeObserver(calculate);
    observer.observe(container);
    container.querySelectorAll('li.overflow-tab').forEach(tab => observer.observe(tab));
    return () => observer.disconnect();
  }, [items, activeTab]);

  const isHidden = (index: number) => index >= visibleCount && items[index].id !== activeTab;
  const overflow = items.filter((_, index) => isHidden(index));

  // the "..." menu opens on hover; the short delay lets the pointer travel from the toggle to the menu
  const openMenu = () => {
    clearTimeout(closeTimer.current);
    setMenuOpen(true);
  };
  const closeMenu = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMenuOpen(false), 150);
  };
  // touch screens emulate hover, so only a real mouse opens and closes the menu by hovering
  const onHover = (action: () => void) => (event: React.PointerEvent) => event.pointerType === 'mouse' && action();

  // a click or tap on the "..." itself only opens the menu (hover already opened it on desktop);
  // clicks outside and Escape close it
  const closeOnOutside = (event?: React.SyntheticEvent | Event) => {
    const target = event?.target as HTMLElement | undefined;
    if (target?.closest?.('.overflow-tabs-more .nav-link')) return;
    setMenuOpen(false);
  };

  return (
    <div ref={containerRef} className="overflow-tabs">
      <Nav tabs>
        {items.map((item, index) => (
          <NavItem key={item.id} className={classnames('overflow-tab', { 'overflow-tab-hidden': isHidden(index) })}>
            <NavLink className={classnames({ active: activeTab === item.id })} onClick={() => onSelect(item.id)}>
              {item.icon && <img src={item.icon} alt="icon" />} {item.label}
            </NavLink>
          </NavItem>
        ))}
        {overflow.length > 0 && (
          <NavItem className="overflow-tabs-more" onPointerEnter={onHover(openMenu)} onPointerLeave={onHover(closeMenu)}>
            <Dropdown isOpen={menuOpen} toggle={closeOnOutside}>
              <DropdownToggle tag="a" className="nav-link" role="button" aria-label="More tabs" onClick={openMenu}>
                ...
              </DropdownToggle>
              <DropdownMenu end>
                {overflow.map(item => (
                  <DropdownItem
                    key={item.id}
                    onClick={() => {
                      setMenuOpen(false);
                      onSelect(item.id);
                    }}
                  >
                    {item.label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </NavItem>
        )}
      </Nav>
    </div>
  );
};

export default OverflowTabs;
